/* Bijverkoopflow (Music All In) v2 - zie snippets/bijverkoop-flow.liquid */
import { ThemeEvents, CartAddEvent } from '@theme/events';
const MAX_CHOICES = 3;
const LABEL_OVERVIEW = 'Overzicht';
const eur = (cents) =>
(Number(cents || 0) / 100).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
const esc = (value) =>
String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
const root = () => window.Shopify?.routes?.root || '/';
const tipHtml = (tip) => (tip ? `<p class="bv__tip"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"></circle><path d="M12 11v5M12 8h.01"></path></svg><span>${esc(tip)}</span></p>` : '');
class BijverkoopFlow extends HTMLElement {
connectedCallback() {
const raw = this.querySelector('[data-bv-config]');
if (!raw) return;
try {
this.config = JSON.parse(raw.textContent);
} catch (error) {
console.warn('Bijverkoopflow: data niet leesbaar', error);
return;
}
this.steps = this.#buildSteps(this.config.items || []);
if (this.steps.length === 0) return;
this.dialog = this.#createDialog();
document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
}
disconnectedCallback() {
document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
this.dialog?.remove();
}
#buildSteps(items) {
const byHandle = new Map();
for (const item of items) {
if (!item || !item.available || !item.step) continue;
const key = item.step.handle;
if (!byHandle.has(key)) byHandle.set(key, { ...item.step, items: [] });
byHandle.get(key).items.push(item);
}
return [...byHandle.values()]
.sort((a, b) => Number(a.order) - Number(b.order))
.map((step) => {
const free = step.items.find((i) => Number(i.price) === 0);
if (free) {
const sorted = [...step.items].sort((a, b) => Number(b.price) - Number(a.price));
return { ...step, kind: 'choice', items: sorted.slice(0, MAX_CHOICES) };
}
return { ...step, kind: 'product', items: step.items.slice(0, 1) };
});
}
#onCartUpdate = (event) => {
const data = event?.detail?.data || {};
if (data.source !== 'product-form-component') return;
if (data.didError) return;
if (String(data.productId) !== String(this.dataset.productId)) return;
setTimeout(() => {
document.querySelector('cart-drawer-component')?.close?.();
this.open();
}, 0);
};
open() {
this.index = 0;
this.added = new Map();
this.choice = new Map();
this.busy = false;
this.error = null;
this.render();
if (!this.dialog.open) this.dialog.showModal();
this.dialog.querySelector('.bv__panel')?.focus();
}
async close() {
if (this.dialog.open) this.dialog.close();
if (this.added.size > 0) this.#refreshCartSilently();
}
#isOverview() {
return this.index >= this.steps.length;
}
#chosenItems() {
const out = [];
this.steps.forEach((step, i) => {
step.items.forEach((item) => {
const picked = step.kind === 'choice' ? this.choice.get(i) === item.variantId : this.added.has(item.variantId);
if (picked) out.push({ step, item });
});
});
return out;
}
#total() {
return this.#chosenItems().reduce((sum, { item }) => sum + Number(item.price), Number(this.config.main.price));
}
async #cartAdd(variantId) {
const response = await fetch(`${root()}cart/add.js`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
body: JSON.stringify({
items: [{ id: variantId, quantity: 1, properties: { _bijverkoop: this.config.main.handle } }],
}),
});
const result = await response.json();
if (!response.ok || result.status) throw new Error(result.description || result.message || 'Toevoegen mislukt');
const line = (result.items || [])[0];
this.added.set(variantId, line?.key || String(variantId));
}
async #cartRemove(variantId) {
const key = this.added.get(variantId);
if (!key) return;
const response = await fetch(`${root()}cart/change.js`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
body: JSON.stringify({ id: key, quantity: 0 }),
});
if (!response.ok) throw new Error('Verwijderen mislukt');
this.added.delete(variantId);
}
async #syncChoice(stepIndex) {
const step = this.steps[stepIndex];
const chosen = this.choice.get(stepIndex);
for (const item of step.items) {
if (item.variantId !== chosen && this.added.has(item.variantId)) await this.#cartRemove(item.variantId);
}
if (chosen && !this.added.has(chosen)) await this.#cartAdd(chosen);
}
#refreshCartSilently() {
const drawer = document.querySelector('cart-drawer-component');
const hadAutoOpen = drawer?.hasAttribute('auto-open');
if (hadAutoOpen) drawer.removeAttribute('auto-open');
document.dispatchEvent(new CartAddEvent({}, 'bijverkoop-flow', { source: 'bijverkoop-flow' }));
if (hadAutoOpen) setTimeout(() => drawer.setAttribute('auto-open', ''), 0);
}
async #run(task) {
if (this.busy) return;
this.busy = true;
this.error = null;
this.render();
try {
await task();
} catch (error) {
console.error(error);
this.error = 'Er ging iets mis met de winkelmand. Probeer het opnieuw of bel ons even.';
}
this.busy = false;
this.render();
}
#go(index) {
this.index = Math.max(0, Math.min(index, this.steps.length));
this.render();
this.dialog.querySelector('.bv__layout')?.scrollTo?.(0, 0);
}
#createDialog() {
const dialog = document.createElement('dialog');
dialog.className = 'bv';
dialog.setAttribute('aria-labelledby', 'bv-title');
dialog.addEventListener('click', (event) => this.#onClick(event));
dialog.addEventListener('cancel', (event) => {
event.preventDefault();
this.close();
});
document.body.appendChild(dialog);
return dialog;
}
#onClick(event) {
if (event.target === this.dialog) return this.close();
const el = event.target.closest('[data-bv-action]');
if (!el || this.busy) return;
const action = el.dataset.bvAction;
const step = this.steps[this.index];
const item = step?.items[0];
switch (action) {
case 'close':
this.close();
break;
case 'add':
this.#run(async () => {
if (!this.added.has(item.variantId)) await this.#cartAdd(item.variantId);
this.#go(this.index + 1);
});
break;
case 'remove':
this.#run(() => this.#cartRemove(item.variantId));
break;
case 'skip':
this.#run(async () => {
if (this.added.has(item.variantId)) await this.#cartRemove(item.variantId);
this.#go(this.index + 1);
});
break;
case 'next':
if (step?.kind === 'choice') {
this.#run(async () => {
await this.#syncChoice(this.index);
this.#go(this.index + 1);
});
} else {
this.#go(this.index + 1);
}
break;
case 'pick':
this.choice.set(this.index, Number(el.dataset.variantId));
this.render();
break;
case 'back':
this.#go(this.index - 1);
break;
case 'goto':
if (Number(el.dataset.index) < this.index) this.#go(Number(el.dataset.index));
break;
case 'continue':
this.#run(async () => {
for (let i = 0; i < this.steps.length; i++) {
if (this.steps[i].kind === 'choice') await this.#syncChoice(i);
}
this.close();
});
break;
case 'checkout':
this.#run(async () => {
for (let i = 0; i < this.steps.length; i++) {
if (this.steps[i].kind === 'choice') await this.#syncChoice(i);
}
window.location.href = `${root()}checkout`;
await new Promise(() => {});
});
break;
}
}
#productBody(step) {
const item = step.items[0];
const isAdded = this.added.has(item.variantId);
return `
<article class="bv__product${isAdded ? ' is-added' : ''}">
<div class="bv__product-media">${
item.image ? `<img src="${esc(item.image)}" alt="" width="320" height="260">` : ''
}</div>
<div class="bv__product-info">
<h3 class="bv__product-title">${esc(item.title)}</h3>
${item.text ? `<p class="bv__product-text">${esc(item.text)}</p>` : ''}
<p class="bv__product-price">+ ${eur(item.price)}</p>
${
isAdded
? `<p class="bv__product-added">✓ Zit in je winkelmand · <button type="button" class="bv__link bv__link--inline" data-bv-action="remove">Toch niet</button></p>`
: ''
}
${tipHtml(step.tip)}
</div>
</article>`;
}
#choiceBody(step) {
const chosen = this.choice.get(this.index);
const cards = step.items
.map((item) => {
const on = chosen === item.variantId;
const price = Number(item.price) === 0 ? 'Gratis' : eur(item.price);
const perks = (item.perks || []).filter(Boolean);
return `<li><button type="button" class="bv__option${on ? ' is-selected' : ''}${item.label ? ' is-featured' : ''}" role="radio" aria-checked="${on}" data-bv-action="pick" data-variant-id="${item.variantId}">
<span class="bv__radio" aria-hidden="true"></span>
<span class="bv__option-body">
${item.label ? `<span class="bv__option-label">${esc(item.label)}</span>` : ''}
<span class="bv__option-title">${esc(item.title)}</span>
${item.text ? `<span class="bv__option-text">${esc(item.text)}</span>` : ''}
${perks.length ? `<span class="bv__perks">${perks.map((p) => `<span class="bv__perk">${esc(p)}</span>`).join('')}</span>` : ''}
</span>
<span class="bv__option-price">${price}</span>
</button></li>`;
})
.join('');
return `<ul class="bv__options" role="radiogroup" aria-labelledby="bv-title">${cards}</ul>${tipHtml(step.tip)}`;
}
render() {
const total = this.steps.length + 1;
const current = this.index + 1;
const overview = this.#isOverview();
const step = overview ? null : this.steps[this.index];
const chosen = this.#chosenItems();
const main = this.config.main;
const busy = this.busy ? ' disabled' : '';
const nav = [...this.steps.map((s) => s.name), LABEL_OVERVIEW]
.map((label, i) => {
const state = i < this.index ? 'done' : i === this.index ? 'current' : 'todo';
const num = state === 'done' ? '✓' : String(i + 1);
const inner = `<span class="bv__nav-num" aria-hidden="true">${num}</span><span class="bv__nav-label">${esc(label)}</span>`;
return state === 'done'
? `<li><button type="button" class="bv__nav-item bv__nav-item--done" data-bv-action="goto" data-index="${i}">${inner}</button></li>`
: `<li><span class="bv__nav-item bv__nav-item--${state}"${state === 'current' ? ' aria-current="step"' : ''}>${inner}</span></li>`;
})
.join('');
const lines = [
`<li class="bv__line"><span><span class="bv__line-name">${esc(main.title)}</span></span><span class="bv__line-price">${eur(main.price)}</span></li>`,
...chosen.map(
({ step: s, item }) =>
`<li class="bv__line"><span><span class="bv__line-name">${esc(item.title)}</span><span class="bv__line-group">${esc(s.name)}</span></span><span class="bv__line-price">${Number(item.price) === 0 ? 'Gratis' : eur(item.price)}</span></li>`
),
].join('');
let body;
if (overview) {
body = `
<p class="bv__eyebrow">Stap ${current} van ${total}</p>
<h2 id="bv-title" class="bv__title">Je bestelling is compleet</h2>
<p class="bv__intro">Controleer je keuzes. Via de stappen hierboven kun je nog iets aanpassen.</p>
<ul class="bv__lines bv__lines--overview" role="list">${lines}</ul>
<p class="bv__total"><span>Totaal</span><span>${eur(this.#total())}</span></p>`;
} else {
body = `
<p class="bv__eyebrow">Stap ${current} van ${total}</p>
<h2 id="bv-title" class="bv__title">${esc(step.title || step.name)}</h2>
${step.intro ? `<p class="bv__intro">${esc(step.intro)}</p>` : ''}
${step.kind === 'product' ? this.#productBody(step) : this.#choiceBody(step)}`;
}
let actions;
if (overview) {
actions = `<span class="bv__checkout"><button type="button" class="bv__btn bv__btn--primary" data-bv-action="checkout"${busy}>${this.busy ? 'Even geduld…' : 'Afrekenen'}</button>
<button type="button" class="bv__link bv__link--small" data-bv-action="continue"${busy}>Toevoegen en verder winkelen</button></span>`;
} else if (step.kind === 'product') {
const isAdded = this.added.has(step.items[0].variantId);
actions = isAdded
? `<button type="button" class="bv__btn bv__btn--primary" data-bv-action="next"${busy}>${this.index === this.steps.length - 1 ? 'Naar overzicht' : 'Volgende stap'}</button>`
: `<button type="button" class="bv__link" data-bv-action="skip"${busy}>Nee, dank je</button>
<button type="button" class="bv__btn bv__btn--primary" data-bv-action="add"${busy}>${this.busy ? 'Even geduld…' : `Toevoegen · ${eur(step.items[0].price)}`}</button>`;
} else {
const off = this.choice.get(this.index) ? busy : ' disabled';
actions = `<button type="button" class="bv__btn bv__btn--primary" data-bv-action="next"${off}>${this.busy ? 'Even geduld…' : !this.choice.get(this.index) ? 'Maak een keuze' : this.index === this.steps.length - 1 ? 'Naar overzicht' : 'Volgende stap'}</button>`;
}
this.dialog.innerHTML = `
<div class="bv__panel" tabindex="-1">
<header class="bv__head">
<p class="bv__added">
<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"></path></svg>
<span><strong>${esc(main.title)}</strong> zit in je winkelmand. Maak hem in een paar stappen speelklaar.</span>
</p>
<button type="button" class="bv__close" data-bv-action="close" aria-label="Sluiten">
<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"></path></svg>
</button>
</header>
<ol class="bv__nav" role="list">${nav}</ol>
<div class="bv__progress" aria-hidden="true"><span style="width:${Math.round((current / total) * 100)}%"></span></div>
<div class="bv__layout">
<section class="bv__main">${body}${this.error ? `<p class="bv__error" role="alert">${esc(this.error)}</p>` : ''}</section>
<aside class="bv__aside" aria-label="Jouw keuze">
<h3 class="bv__aside-title">Jouw keuze</h3>
<ul class="bv__lines" role="list">${lines}</ul>
<p class="bv__total"><span>Totaal</span><span>${eur(this.#total())}</span></p>
<p class="bv__vat">Inclusief btw</p>
</aside>
</div>
<footer class="bv__foot">
${this.index > 0 ? `<button type="button" class="bv__btn bv__btn--secondary bv__btn--back" data-bv-action="back"${busy}>Terug</button>` : '<span></span>'}
<span class="bv__foot-total">${eur(this.#total())}</span>
<span class="bv__foot-actions">${actions}</span>
</footer>
</div>`;
}
}
if (!customElements.get('bijverkoop-flow')) customElements.define('bijverkoop-flow', BijverkoopFlow);